import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'

interface B2BvsB2CData {
  b2b: {
    revenue: number
    revenueFormatted: string
    transactions: number
    percentage: string
    avgTransaction: string
  }
  b2c: {
    revenue: number
    revenueFormatted: string
    transactions: number
    percentage: string
    avgTransaction: string
  }
  total: {
    revenue: number
    revenueFormatted: string
    transactions: number
  }
}

interface B2BvsB2CWidgetProps {
  data: B2BvsB2CData | null
  loading: boolean
}

const COLORS = ['#3B82F6', '#10B981'] // Blue for B2B, Green for B2C

export function B2BvsB2CWidget({ data, loading }: B2BvsB2CWidgetProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">B2B vs B2C Analytics</h3>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">B2B vs B2C Analytics</h3>
        <p className="text-gray-500">No data available</p>
      </div>
    )
  }

  const chartData = [
    { name: 'B2B', value: data.b2b.revenue, count: data.b2b.transactions },
    { name: 'B2C', value: data.b2c.revenue, count: data.b2c.transactions }
  ]

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-4">B2B vs B2C Analytics</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((_entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any) => `₹${value.toLocaleString('en-IN')}`} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Stats */}
        <div className="space-y-4">
          {/* B2B Stats */}
          <div className="border-l-4 border-blue-500 pl-4">
            <div className="text-sm text-gray-600">B2B Revenue</div>
            <div className="text-xl font-bold text-blue-600">
              {data.b2b.revenueFormatted} ({data.b2b.percentage}%)
            </div>
            <div className="text-sm text-gray-500">
              {data.b2b.transactions} transactions • Avg ₹{data.b2b.avgTransaction}
            </div>
          </div>

          {/* B2C Stats */}
          <div className="border-l-4 border-green-500 pl-4">
            <div className="text-sm text-gray-600">B2C Revenue</div>
            <div className="text-xl font-bold text-green-600">
              {data.b2c.revenueFormatted} ({data.b2c.percentage}%)
            </div>
            <div className="text-sm text-gray-500">
              {data.b2c.transactions} transactions • Avg ₹{data.b2c.avgTransaction}
            </div>
          </div>

          {/* Total */}
          <div className="border-t pt-3">
            <div className="text-sm text-gray-600">Total Revenue</div>
            <div className="text-lg font-semibold">
              {data.total.revenueFormatted}
            </div>
            <div className="text-sm text-gray-500">
              {data.total.transactions} total transactions
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
